import { PlusOutlined } from "@ant-design/icons";
import { Button, Card, Col, Row } from "antd";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import AddDatasetTemplateButton from "@/components/InterventionTemplate/AddDatasetTemplateButton";
import RemoveSectionConfirmation from "@/components/InterventionTemplate/RemoveSectionConfirmation";
import InterventionTemplateSection from "@/components/InterventionTemplate/Section";
import TableOfContents from "@/components/InterventionTemplate/TableOfContents";
import { TreeModifier } from "@/components/InterventionTemplate/TreeModifier";
import {
  CARTESIAN,
  PLAN_POSITIONING,
  FILE,
  FORM,
  GPS,
  IMAGE,
} from "@/constants/DatasetTypes";
import { useUpdate } from "@/hooks/queries";
import { useApiErrorHandler } from "@/modules/api-errors";
import { useSidePanel } from "@/modules/side-panel";

import CartesianRender from "./DatasetRenderers/CartesianRender";
import FileRender from "./DatasetRenderers/FileRender";
import FormRender from "./DatasetRenderers/FormRender";
import GPSRender from "./DatasetRenderers/GPSRender";
import ImageRender from "./DatasetRenderers/ImageRender";
import PlanPositioningRender from "./DatasetRenderers/PlanPositioningRender";
import SectionEditor from "./SectionEditor";
import { cloneNodeAtLayoutIndex } from "./templateEditorUtils";

const TemplateEditor = ({ template, loading }) => {
  const { t } = useTranslation();
  const { openSidePanel } = useSidePanel();
  const { onError } = useApiErrorHandler();
  const [searchParams, setSearchParams] = useSearchParams();

  const isEditable = template?.is_editable ?? false;

  const mutation = useUpdate("interventionTemplates", template?.id);

  const componentLoading = loading;

  const layoutIndex = parseInt(
    searchParams.get("layout_index") ?? template?.layout_index
  );

  /**
   * Update a template at a layout_index
   *
   * @param {number} layoutIndex
   * @param {object} modifiedTemplate
   * @return {void}
   */
  const updateTemplateAtLayoutIndex = (layoutIndex, modifiedTemplate) => {
    const newTree = TreeModifier.findAndReplace(
      template,
      "templates",
      (template) => template.layout_index === layoutIndex,
      modifiedTemplate
    );

    mutation.mutate(
      { templates: newTree.templates },
      {
        onError,
      }
    );
  };

  const onAddDatasetTemplate = (type) => {
    openSidePanel({
      title: t("interventionTemplate:dataset:create:label"),
      component: (
        <AddDatasetTemplateButton
          parentTemplate={template}
          type={type}
          layoutIndex={layoutIndex}
        />
      ),
    });
  };

  const onRemoveTemplateSection = (layoutIndex) => {
    openSidePanel({
      title: t("interventionTemplate:form.section.remove.label"),
      component: (
        <RemoveSectionConfirmation
          template={template}
          layoutIndex={layoutIndex ?? template.layout_index}
        />
      ),
    });
  };

  const onDuplicateTemplateSection = (layoutIndex) => {
    const { updatedTemplate, newNodeId } = cloneNodeAtLayoutIndex({
      template,
      layoutIndex,
    });

    mutation.mutate(
      { templates: updatedTemplate.templates },
      {
        onSuccess: () => setSearchParams({ layout_index: newNodeId }),
        onError,
      }
    );
  };

  const formRender = (formsTemplatesInput) => {
    const formsTemplates = formsTemplatesInput ?? [];
    const hasFormTemplate = formsTemplates.find(
      (template) => template.type === FORM
    );

    return (
      <>
        {formsTemplates.map((formTemplate) => (
          <FormRender
            formTemplate={formTemplate ?? {}}
            onChange={updateTemplateAtLayoutIndex}
            onRemove={onRemoveTemplateSection}
            isEditable={isEditable}
            key={formTemplate?.layout_index}
            mutation={mutation}
          />
        ))}
        {!hasFormTemplate && (
          <Button
            aria-label="addFormTemplate"
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => onAddDatasetTemplate(FORM)}
            disabled={!isEditable}
          >
            {t("interventionTemplate:form.addForm")}
          </Button>
        )}
      </>
    );
  };

  const cartesianRender = (cartesiansTemplates) => {
    return (
      <>
        {cartesiansTemplates?.map((cartesianTemplate) => (
          <CartesianRender
            cartesianTemplate={cartesianTemplate ?? {}}
            onRemove={onRemoveTemplateSection}
            isEditable={isEditable}
            key={cartesianTemplate.layout_index}
          />
        ))}
        {!cartesiansTemplates?.length && (
          <Button
            aria-label="addCartesianTemplate"
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => onAddDatasetTemplate(CARTESIAN)}
            disabled={!isEditable}
          >
            {t("global:add")}
          </Button>
        )}
      </>
    );
  };

  const planPositioningRender = (planPositioningTemplates) => {
    return (
      <>
        {planPositioningTemplates?.map((planPositioningTemplate) => (
          <PlanPositioningRender
            planPositioningTemplate={planPositioningTemplate ?? {}}
            onRemove={onRemoveTemplateSection}
            isEditable={isEditable}
            key={planPositioningTemplate.layout_index}
          />
        ))}
        {!planPositioningTemplates?.length && (
          <Button
            aria-label="addPlanPositioningTemplate"
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => onAddDatasetTemplate(PLAN_POSITIONING)}
            disabled={!isEditable}
          >
            {t("global:add")}
          </Button>
        )}
      </>
    );
  };

  const gpsRender = (gpsTemplates) => {
    return (
      <>
        {gpsTemplates?.map((gpsTemplate) => (
          <GPSRender
            gpsTemplate={gpsTemplate ?? {}}
            onRemove={onRemoveTemplateSection}
            isEditable={isEditable}
            key={gpsTemplate.layout_index}
          />
        ))}
        {!gpsTemplates?.length && (
          <Button
            aria-label="addGpsTemplate"
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => onAddDatasetTemplate(GPS)}
            disabled={!isEditable}
          >
            {t("global:add")}
          </Button>
        )}
      </>
    );
  };

  const imageRender = (imagesTemplates) => {
    return (
      <>
        {imagesTemplates?.map((imageTemplate) => (
          <ImageRender
            imageTemplate={imageTemplate ?? {}}
            onRemove={onRemoveTemplateSection}
            isEditable={isEditable}
            key={imageTemplate.layout_index}
          />
        ))}
        {!imagesTemplates?.length && (
          <Button
            aria-label="addImageTemplate"
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => onAddDatasetTemplate(IMAGE)}
            disabled={!isEditable}
          >
            {t("global:add")}
          </Button>
        )}
      </>
    );
  };

  const chartRender = () => <></>;

  const fileRender = (filesTemplates) => {
    return (
      <>
        <FileRender
          fileTemplates={filesTemplates}
          onRemove={onRemoveTemplateSection}
          isEditable={isEditable}
        />
        <Button
          aria-label="addFileTemplate"
          type="dashed"
          icon={<PlusOutlined />}
          onClick={() => onAddDatasetTemplate(FILE)}
          disabled={!isEditable}
        >
          {t("global:add")}
        </Button>
      </>
    );
  };

  const renderers = {
    formRender,
    cartesianRender,
    planPositioningRender,
    gpsRender,
    imageRender,
    chartRender,
    fileRender,
  };

  return (
    <>
      <Row justify="center">
        <Col span={24}>
          <Row gutter={16} justify="center">
            <Col
              xs={22}
              sm={22}
              md={22}
              lg={11}
              style={{ marginBottom: "10px" }}
            >
              <Card title={t("interventionTemplate:tableOfContents")}>
                {!componentLoading && (
                  <TableOfContents
                    menu={template ? [template] : null}
                    childrenKey="templates"
                  />
                )}
              </Card>
            </Col>
            <Col xs={22} sm={22} md={22} lg={11}>
              {template?.id && (
                <InterventionTemplateSection
                  {...renderers}
                  interventionTemplateId={template.id}
                  parentLoading={componentLoading}
                  cardProps={{
                    extra: (
                      <SectionEditor
                        template={template}
                        onRemoveSection={onRemoveTemplateSection}
                        onDuplicateSection={onDuplicateTemplateSection}
                      />
                    ),
                  }}
                />
              )}
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
};

TemplateEditor.propTypes = {
  template: PropTypes.object,
  loading: PropTypes.bool,
};

export default TemplateEditor;
